var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useRef, useState, useMemo, useCallback } from 'react';
import { Accordion, useAccordion, Copy, Box } from '@honda-canada/design-system-react';
import debounce from 'lodash/debounce';
import Decade from './Decade';
import { getDecadeTitle, getHrefValue, onTimelineNavClick } from './service/utils';
import YearsProgressBar from './YearsProgressBar';
import TimelineItem from './shared/TimelineItem';
// import themeStyles, { ScrollSnapGlobalCss } from './Timeline.styles';
import themeStyles from './Timeline.styles';
import { COLLAPSE_ACCORDION, EXPAND_ACCORDION, ACCORDION_MODE_DEFAULT, ACCORDION_MODE_SCROLL, ACCORDION_TITLE_HEIGHT, } from './shared/constants';
var AccordionTitleText = themeStyles.apply(Copy, 'AccordionTitleText');
var AccordionTitle = function (_a) {
    var title = _a.title, yearProgressBar = _a.yearProgressBar;
    return (React.createElement(React.Fragment, null,
        React.createElement(AccordionTitleText, { size: "accordionLarge", fontWeight: "bold", pl: ['m', 'default'] }, getDecadeTitle(title)),
        yearProgressBar));
};
var MobileTimeline = function (_a) {
    var _b;
    var decades = _a.decades, setCurrentYear = _a.setCurrentYear, setCurrentDecade = _a.setCurrentDecade, currentYear = _a.currentYear, currentDecade = _a.currentDecade, currentDecadeValue = _a.currentDecadeValue, allYears = _a.allYears;
    var _c = useState((_b = decades[0]) === null || _b === void 0 ? void 0 : _b.value), currentToggle = _c[0], setCurrentToggle = _c[1];
    var currentToggleDecade = decades === null || decades === void 0 ? void 0 : decades.find(function (_a) {
        var value = _a.value;
        return value === currentToggle;
    });
    var _d = useState(true), isYearsVisible = _d[0], setIsYearsVisible = _d[1];
    var activeDecade = decades === null || decades === void 0 ? void 0 : decades.find(function (_a) {
        var value = _a.value;
        return value === currentDecadeValue;
    });
    var headerRef = useRef(null);
    var containerRef = useRef(null);
    // const { inView: mobileInview } = useTimelineViewArea({ headerRef, bottomRef: containerRef, isMobile: true });
    var getProgress = function () {
        var _a;
        var currentDecadeYearLength = (_a = currentDecade === null || currentDecade === void 0 ? void 0 : currentDecade.years) === null || _a === void 0 ? void 0 : _a.length;
        if (!currentDecadeYearLength)
            return 0;
        var currentYearIndex = currentDecade.years.findIndex(function (year) { return year.value === (currentYear === null || currentYear === void 0 ? void 0 : currentYear.value); });
        if (currentYearIndex < 0)
            return 0;
        return ((currentYearIndex + 1) / currentDecadeYearLength) * 100;
    };
    var debouncedSetCurrentYear = useMemo(function () { return debounce(setCurrentYear, 300); }, [setCurrentYear]);
    var setYear = useCallback(function (year) {
        debouncedSetCurrentYear.cancel();
        debouncedSetCurrentYear(year);
    }, [debouncedSetCurrentYear]);
    var accordionMode = currentToggle && activeDecade ? ACCORDION_MODE_SCROLL : ACCORDION_MODE_DEFAULT;
    var accordionItems = currentToggleDecade && activeDecade
        ? // when accordion is open, show the all years for scrolling
            decades.map(function (decade) {
                var _a, _b;
                var isActiveDecade = activeDecade.value === decade.value;
                var isCurrentToggle = currentToggleDecade.value === decade.value;
                var ariaStatus = {
                    'aria-pressed': "".concat(isActiveDecade),
                    'aria-expanded': "".concat(isActiveDecade),
                };
                return {
                    key: decade.value,
                    id: decade.value,
                    accordionItemStyles: isCurrentToggle ? {} : { display: 'none !important' },
                    gtmTitle: (_a = decade.mobileGtm.gtm) === null || _a === void 0 ? void 0 : _a.title,
                    gtmTags: decade.mobileGtm.gtm,
                    title: (React.createElement(AccordionTitle, { title: isCurrentToggle ? activeDecade === null || activeDecade === void 0 ? void 0 : activeDecade.value : decade.value, yearProgressBar: isCurrentToggle ? (React.createElement(YearsProgressBar, { setRef: function (ref) {
                                headerRef.current = ref;
                            }, progress: currentToggle ? getProgress() : 0 })) : undefined })),
                    content: (React.createElement(Decade, { gtmTags: __assign(__assign({}, decade.mobileGtm.gtm), { otherGtmTags: __assign(__assign({}, decade.mobileGtm.componentParameters), ariaStatus), interactionType: isActiveDecade ? EXPAND_ACCORDION : COLLAPSE_ACCORDION }) }, (_b = (isCurrentToggle ? allYears : decade.years)) === null || _b === void 0 ? void 0 : _b.map(function (year) {
                        return year.items.map(function (item) { return (React.createElement(TimelineItem, { key: item.title, isVisible: isYearsVisible || year.decadeValue === activeDecade.value, ariaHidden: isCurrentToggle && year.decadeValue !== activeDecade.value, year: year, item: item, decadeValue: activeDecade === null || activeDecade === void 0 ? void 0 : activeDecade.value, setCurrentYear: setYear })); });
                    }))),
                };
            })
        : // when accordion is closed, show all decades's titles
            decades === null || decades === void 0 ? void 0 : decades.map(function (decade) {
                var _a, _b;
                return ({
                    key: decade.value,
                    id: decade.value,
                    // ensure that when scroll happens, the item is visible
                    accordionItemStyles: { scrollMarginTop: ACCORDION_TITLE_HEIGHT },
                    gtmTitle: (_a = decade.mobileGtm.gtm) === null || _a === void 0 ? void 0 : _a.title,
                    gtmTags: decade.mobileGtm.gtm,
                    title: React.createElement(AccordionTitle, { title: decade.value }),
                    content: (React.createElement(Decade, { key: decade.value, gtmTags: __assign(__assign({}, decade.mobileGtm.gtm), { otherGtmTags: decade.mobileGtm.componentParameters, interactionType: currentToggle && decade.value === currentDecadeValue ? EXPAND_ACCORDION : COLLAPSE_ACCORDION }) }, (_b = decade === null || decade === void 0 ? void 0 : decade.years) === null || _b === void 0 ? void 0 : _b.map(function (year) {
                        return year.items.map(function (item) { return (React.createElement(TimelineItem, { key: item.title, year: year, item: item, decadeValue: decade === null || decade === void 0 ? void 0 : decade.value, setCurrentYear: setCurrentYear })); });
                    }))),
                });
            });
    var accordionBehaviour = useAccordion(accordionItems, false, [currentDecadeValue]);
    var toggleItem = accordionBehaviour.toggleItem, isActive = accordionBehaviour.isActive;
    var onGTMEventSend = function (decade, interactionType) {
        var availableDecade = activeDecade || decade;
        if (!availableDecade)
            return;
        var _a = availableDecade.mobileGtm, gtm = _a.gtm, gtmEvent = _a.gtmEvent;
        if (!gtmEvent)
            return;
        if (!gtm)
            return;
        gtm.interactionType = interactionType;
        gtmEvent(gtm);
    };
    var onToggleItem = function (itemName) {
        var _a;
        // toggle item from behaviour
        toggleItem(itemName);
        var availableDecade = decades === null || decades === void 0 ? void 0 : decades.find(function (_a) {
            var value = _a.value;
            return value === itemName;
        });
        var isClosingCurrentToogle = itemName == currentToggle && isActive(itemName !== null && itemName !== void 0 ? itemName : 0);
        // FIXME: GTM logic needs to be updated after refactoring
        var interactionType = itemName !== (activeDecade === null || activeDecade === void 0 ? void 0 : activeDecade.value) ? EXPAND_ACCORDION : COLLAPSE_ACCORDION;
        onGTMEventSend(availableDecade, interactionType);
        if (isClosingCurrentToogle) {
            setCurrentToggle(undefined);
            // when closing the current decade accordion, reset current decade and year
            setCurrentYear(undefined, true);
            // FIXME: use better navigation no other option currently
            // Navigate to the top of the accordion that has the current decade
            onTimelineNavClick("".concat(currentDecadeValue));
            return;
        }
        if (!isClosingCurrentToogle) {
            setCurrentToggle(itemName);
            setIsYearsVisible(false);
            // when opening a new decade accordion, update currentDecade and scroll to the first year
            if (availableDecade) {
                setCurrentDecade(availableDecade);
                onTimelineNavClick(getHrefValue(availableDecade.value, (_a = availableDecade.years[0]) === null || _a === void 0 ? void 0 : _a.value));
                // TODO find a better way for opening the first year for a decade
                // the timeout simulates the same time out for the navigation which in turn
                // simulates the same timeout for height of the accordion children
                setTimeout(function () {
                    setIsYearsVisible(true);
                }, 200);
            }
        }
    };
    var modifiedAccordionBehaviour = __assign(__assign({}, accordionBehaviour), { toggleItem: onToggleItem });
    return (React.createElement(Box, { mb: 16, ref: containerRef },
        React.createElement(Accordion, { isTitleSticky: true, titleOffset: 45, isCompact: true, itemOverflow: !!currentToggle, items: accordionItems, behaviour: modifiedAccordionBehaviour, key: accordionMode })));
};
export default MobileTimeline;
